import { useQuery } from '@tanstack/react-query';
import { BookOpen, LogIn, School } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { KnowledgeArticleView, KnowledgeBrowser } from '../../components/knowledge';
import { Logo } from '../../components/Layout';
import { EmptyState, ErrorState, PageHeader, PageLoader, Spinner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePublicSchool } from '../../lib/branding';
import { publicKnowledgeImageUrl, type KnowledgeArticle, type KnowledgePage, type KnowledgeTagCounts } from '../../lib/knowledge';
import { currentSchool } from '../../lib/school';

const slug = currentSchool ?? '';

/** The school's public help pages at /{school}/help: its name and logo, and a way to sign in. */
function PublicShell({ children }: { children: ReactNode }) {
  const school = usePublicSchool();
  const { me } = useAuth();

  if (school.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-7" />
      </div>
    );
  }
  if (school.error || !school.data) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <EmptyState icon={<School />} title="School not found" description={`There’s no school at /${currentSchool}. Check the address you were given.`} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4 sm:px-6">
          <Link to="/help" className="flex min-w-0 items-center gap-3">
            {school.data.logoUrl ? <img src={school.data.logoUrl} alt="" className="size-9 shrink-0 rounded-lg object-contain" /> : <Logo />}
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-sm font-semibold text-slate-900">{school.data.name}</span>
              <span className="block text-xs text-slate-500">Knowledgebase</span>
            </span>
          </Link>
          <Link
            to={me?.tenant ? '/knowledge' : '/login'}
            className="ml-auto inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <LogIn className="size-4" />
            {me?.tenant ? 'Open Edvora' : 'Sign in'}
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:py-8">{children}</main>
    </div>
  );
}

export function PublicKnowledgePage() {
  const school = usePublicSchool();
  return (
    <PublicShell>
      <PageHeader title="Knowledgebase" description={`How-tos and answers from ${school.data?.name ?? 'the school'}’s technology team.`} />
      <KnowledgeBrowser
        queryKey={['public-knowledge', slug]}
        fetchList={(filters) => api.get<KnowledgePage>(`/public/schools/${slug}/knowledge`, { ...filters, visibility: undefined, mine: undefined })}
        fetchTags={() => api.get<KnowledgeTagCounts>(`/public/schools/${slug}/knowledge/tags`)}
        articleHref={(id) => `/help/${id}`}
        empty={<EmptyState icon={<BookOpen />} title="No public articles yet" description="Sign in to see the articles for students and staff." />}
      />
    </PublicShell>
  );
}

export function PublicKnowledgeArticlePage() {
  const { id } = useParams();
  const article = useQuery({
    queryKey: ['public-knowledge', slug, 'article', id],
    queryFn: () => api.get<KnowledgeArticle>(`/public/schools/${slug}/knowledge/${id}`),
    retry: false,
  });
  return (
    <PublicShell>
      {article.isLoading ? (
        <PageLoader />
      ) : article.error instanceof ApiError && article.error.status === 404 ? (
        <EmptyState
          icon={<BookOpen />}
          title="Article not found"
          description="It may have been removed, or it’s only for people at the school. Sign in to see the articles meant for you."
          action={
            <Link to="/help" className="text-sm font-medium text-brand-700 hover:underline">
              Browse public articles
            </Link>
          }
        />
      ) : article.error || !article.data ? (
        <ErrorState error={article.error} onRetry={() => article.refetch()} />
      ) : (
        <KnowledgeArticleView article={article.data} imageSrc={(imageId) => publicKnowledgeImageUrl(slug, imageId)} listPath="/help" />
      )}
    </PublicShell>
  );
}
